import { memo, useCallback } from 'react'
import { Box, Typography, Grid, IconButton } from '@mui/material'
import CommonModal from '../../../atoms/modal/CommonModal'
import ButtonCustom from '../../../atoms/button/ButtonCustom'
import InputCustom from '../../../atoms/input/InputCustom'
import SelectCustom from '../../../atoms/select/SelectCustom'
import CheckBoxCustom from '../../../atoms/checkbox/CheckBox'
import TextArea from '../../../atoms/textarea/TextArea'
import ImageUploader from '../../../atoms/image-uploader/ImageUploader'
import {
	PACKAGE_PRICE_TYPE_OPTIONS,
	ADDON_TYPE_OPTIONS,
} from '../../../../util/constants'

const AddOnModal = memo(
	({
		open,
		onClose,
		isEditing,
		formData,
		onFormDataChange,
		onImageChange,
		onSave,
		packages = [],
		onConstraintAdd,
		onConstraintRemove,
		onConstraintChange,
		isLoading,
	}) => {
		const handleSave = useCallback(() => {
			onSave()
		}, [onSave])

		return (
			<CommonModal
				open={open}
				handleClose={onClose}
				heading={isEditing ? 'Edit Add-On' : 'Create Add-On'}
				footerContent={
					<>
						<ButtonCustom
							variant="outlined"
							size="lg"
							onClick={onClose}
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="default"
							size="lg"
							onClick={handleSave}
							disabled={isLoading}
						>
							{isLoading
								? 'Saving...'
								: isEditing
									? 'Save Changes'
									: 'Create Add-On'}
						</ButtonCustom>
					</>
				}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						maxHeight: '70vh',
						overflowY: 'auto',
						pr: 1,
					}}
				>
					<ImageUploader
						label="Add-On Thumbnail"
						value={formData.image || formData.imagePreview || null}
						onChange={onImageChange}
						accept="image/*"
						multiple={false}
						width="120px"
						height="120px"
						labelStyle={{
							fontWeight: '400',
							color: '#8692A4',
						}}
					/>

					<Box>
						<Typography
							variant="body2"
							sx={{
								mb: 1,
								fontSize: '14px',
								fontWeight: '400',
								color: '#8692A4',
							}}
						>
							Add-On Type*
						</Typography>
						<SelectCustom
							label="Add-On Type"
							options={ADDON_TYPE_OPTIONS}
							defaultValue={formData.type}
							setDataFunction={(option) =>
								onFormDataChange({
									...formData,
									type: option?.value || '',
								})
							}
						/>
					</Box>
					<InputCustom
						label="Add-On Name*"
						placeholder="e.g., Extra Hour"
						value={formData.name}
						name="name"
						data={formData}
						setDataFunction={onFormDataChange}
						labelStyle={{
							fontWeight: '400',
							color: '#8692A4',
						}}
					/>
					<Box
						sx={{
							bgcolor: '#DFE4EB',
							borderRadius: '12px',
							padding: '12px',
						}}
					>
						<Typography
							variant="body2"
							sx={{
								mb: 1,
								fontSize: '14px',
								fontWeight: '400',
								color: '#313131',
							}}
						>
							Attach to Package(s)
						</Typography>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'repeat(2, 1fr)',
								gap: '8px',
								'@media (max-width: 600px)': {
									gridTemplateColumns: '1fr',
								},
							}}
						>
							{packages?.length > 0 &&
								packages.map((pkg, index) => (
									<Box
										sx={{
											bgcolor: '#fff',
											borderRadius: '8px',
											padding: '10px 12px',
										}}
									>
										<CheckBoxCustom
											key={pkg?._id || index}
											label={
												pkg.name ||
												`Package ${index + 1}`
											}
											checked={
												!!pkg._id &&
												formData.packageIds.includes(
													pkg._id
												)
											}
											onChange={(e) => {
												if (!pkg._id) return
												if (e.target.checked) {
													onFormDataChange({
														...formData,
														packageIds: [
															...formData.packageIds,
															pkg._id,
														],
													})
												} else {
													onFormDataChange({
														...formData,
														packageIds:
															formData.packageIds.filter(
																(id) =>
																	id !==
																	pkg._id
															),
													})
												}
											}}
											sx={{
												'&.MuiButtonBase-root': {
													margin: '0 8px 0 0 !important',
												},
												'& .MuiTypography-root': {
													display: '-webkit-box',
													maxWidth: '100%',
													'-webkit-line-clamp': '1',
													'-webkit-box-orient':
														'vertical',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
												},
											}}
										/>
									</Box>
								))}
						</Box>
					</Box>
					<Box>
						<Typography
							variant="body2"
							sx={{
								mb: 1,
								fontSize: '14px',
								fontWeight: '400',
								color: '#8692A4',
							}}
						>
							Description*
						</Typography>
						<TextArea
							placeholder="Describe this add-on..."
							value={formData.description}
							onChange={(e) =>
								onFormDataChange({
									...formData,
									description: e.target.value,
								})
							}
							sx={{ minHeight: '100px' }}
						/>
					</Box>
					<Box>
						<Typography
							variant="body2"
							sx={{
								mb: 1,
								fontSize: '14px',
								fontWeight: '400',
								color: '#8692A4',
							}}
						>
							Price Type*
						</Typography>
						<SelectCustom
							label="Price Type"
							options={PACKAGE_PRICE_TYPE_OPTIONS}
							defaultValue={formData.priceType}
							setDataFunction={(option) =>
								onFormDataChange({
									...formData,
									priceType: option?.value || '',
								})
							}
						/>
					</Box>
					<InputCustom
						label="Price*"
						placeholder="0.00"
						value={formData.price}
						name="price"
						type="number"
						data={formData}
						setDataFunction={(value) => {
							if (value?.price >= 0 && value?.price <= 99999)
								onFormDataChange(value)
						}}
						labelStyle={{
							fontWeight: '400',
							color: '#8692A4',
						}}
					/>
					<Grid container spacing={2}>
						<Grid size={{ xs: 6, md: 6 }}>
							<InputCustom
								label="Min Quantity (optional)"
								placeholder="e.g., 1"
								value={formData.minQuantity}
								name="minQuantity"
								type="number"
								data={formData}
								setDataFunction={(value) => {
									if (
										value?.minQuantity >= 0 &&
										value?.minQuantity <= 99999
									)
										onFormDataChange(value)
								}}
								labelStyle={{
									fontWeight: '400',
									color: '#8692A4',
								}}
							/>
						</Grid>
						<Grid size={{ xs: 6, md: 6 }}>
							<InputCustom
								label="Max Quantity (optional)"
								placeholder="e.g., 10"
								value={formData.maxQuantity}
								name="maxQuantity"
								type="number"
								data={formData}
								setDataFunction={(value) => {
									if (
										value?.maxQuantity >= 0 &&
										value?.maxQuantity <= 99999
									)
										onFormDataChange(value)
								}}
								labelStyle={{
									fontWeight: '400',
									color: '#8692A4',
								}}
							/>
						</Grid>
					</Grid>
					<CheckBoxCustom
						label="This add-on is required"
						checked={formData.isThisAddOnIsRequired}
						onChange={(e) =>
							onFormDataChange({
								...formData,
								isThisAddOnIsRequired: e.target.checked,
							})
						}
						sx={{
							'&.MuiButtonBase-root': {
								margin: '0 8px 0 0 !important',
							},
							'& .MuiTypography-root': {
								display: '-webkit-box',
								maxWidth: '100%',
								'-webkit-line-clamp': '1',
								'-webkit-box-orient': 'vertical',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							},
						}}
					/>
					<Box>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								mb: 1,
							}}
						>
							<Typography
								variant="body2"
								sx={{
									fontSize: '14px',
									fontWeight: '400',
									color: '#8692A4',
								}}
							>
								Availability Constraints*
							</Typography>
							<ButtonCustom
								variant="outlined"
								size="md"
								onClick={onConstraintAdd}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
								}}
							>
								<span style={{ fontSize: '16px' }}>+</span>
								Add Constraint
							</ButtonCustom>
						</Box>
						{formData.constraints.length === 0 ? (
							<Typography
								variant="body2"
								sx={{
									color: '#999999',
									fontSize: '14px',
									mb: 1,
								}}
							>
								No constraints added.
							</Typography>
						) : (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 1,
									mb: 1,
								}}
							>
								{formData.constraints.map(
									(constraint, index) => (
										<Box
											key={index}
											sx={{
												display: 'flex',
												gap: 1,
												alignItems: 'center',
											}}
										>
											<InputCustom
												placeholder="Label (e.g., Travel Radius)"
												value={constraint.label || ''}
												name="label"
												data={constraint}
												setDataFunction={(data) =>
													onConstraintChange(
														index,
														'label',
														data.label || ''
													)
												}
											/>
											<InputCustom
												placeholder="Value (e.g., 50 miles)"
												value={constraint.value || ''}
												name="value"
												data={constraint}
												setDataFunction={(data) =>
													onConstraintChange(
														index,
														'value',
														data.value || ''
													)
												}
											/>
											<IconButton
												size="small"
												onClick={() =>
													onConstraintRemove(index)
												}
												sx={{ color: '#D32F2F' }}
											>
												<img
													src="/images/trash-red.svg"
													alt="remove"
													style={{
														width: '16px',
														height: '16px',
													}}
												/>
											</IconButton>
										</Box>
									)
								)}
							</Box>
						)}
					</Box>
				</Box>
			</CommonModal>
		)
	}
)

AddOnModal.displayName = 'AddOnModal'

export default AddOnModal
